import { ElementRef, OnInit, ViewChild, Component } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { Client } from 'src/app/shared/models/Client';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from '../auth.service';
import { NavigationService } from 'src/app/shared/navigation.service';
import { Option } from 'src/app/shared/form-group-select/form-group-select.component';
import { TranslateService } from '@ngx-translate/core';
import { Company } from 'src/app/shared/models/Company';
import { Country } from 'src/app/shared/models/Country';
import { Router } from '@angular/router';
import { Village } from 'src/app/shared/models/Village';
import { AccountService } from 'src/app/account/shared/account.service';
import { Survey } from 'src/app/shared/models/Survey';
import { Language } from '../language';
import { LOCAL_STORAGE } from '../constants';
import { JwtHelperService } from '@auth0/angular-jwt';
import { ValidationUtils } from 'src/app/shared/validation.utils';
import { environment } from 'src/environments/environment';

@Component({
    selector: 'app-register',
    templateUrl: './register.component.html',
    styleUrls: ['./register.component.css'],
    standalone: false
})
export class RegisterComponent implements OnInit {
  @ViewChild('inputLocation') inputLocation?: ElementRef;
  @ViewChild('turnstile') turnstile: any;

  protected readonly environment = environment;

  public languages = Language.getAvailableLanguages();

  public registerForm: FormGroup;
  public errorArray = new Array();
  public companyErrorArray = new Array();
  public currentDate = new Date();
  public languageOptions: Option[] = [
  { value: 'de', label: 'register.german' },
  { value: 'it', label: 'register.italian' },
  { value: 'en', label: 'register.english' }
];
  public titleOptions: Option[] = [
    { value: 'Herr', label: 'register.herr' },
    { value: 'Frau', label: 'register.frau' }
  ];

  public surveys: Survey[] = [];

  public currentLanguage: any;
  public chosenVillage: Village;
  public chosenCountry: Country;
  public missingFirstNameErrorMsg = '';
  public missingLastNameErrorMsg = '';
  public missingEmailErrorMsg = '';
  public missingPasswordErrorMsg = '';
  public retypePasswordErrorMsg = '';
  public missingPhoneNumberErrorMsg = '';
  public missingOrganizationErrorMsg = '';
  public missingVillageErrorMsg = '';
  public missingSurveyErrorMsg = '';
  public missingTermsErrorMsg = '';

  public locationGroupClass = 'col';
  public surveyGroupClass = 'row';

  public inputActive = false;

  public siteKey: string = environment.captchaSiteKey;
  public captchaToken: string;

  constructor(private _toastr: ToastrService, private _formBuilder: FormBuilder, private _authService: AuthService,
    private _navigation: NavigationService, private _router: Router, private _translate: TranslateService,
    private _accountService: AccountService, private _jwtHelper: JwtHelperService
    ) {
    this.currentLanguage = this._translate.currentLang;
    this.registerForm = this._formBuilder.group({
      firstEmail: ['', [<any>Validators.required, <any>Validators.email]],
      password: ['', [<any>Validators.required, <any>Validators.minLength(6)]],
      retypePassword: ['', <any>Validators.required],
      newsletter: [false],
      termsConditionsCheck: [false],
      termsConditionsCheckPoint4: [true],
      termsConditionsCheckPoint6: [true],
      termsConditionsCheckPoint9: [true],
      survey: [0],
      surveyDescription: ['']
    }, {
      validators: [
        this.checkTermsAndConditions('termsConditionsCheck'),
        this.checkTermsAndConditions('termsConditionsCheckPoint4'),
        this.checkTermsAndConditions('termsConditionsCheckPoint6'),
        this.checkTermsAndConditions('termsConditionsCheckPoint9'),
        ValidationUtils.checkIfMatchingPasswords('password', 'retypePassword')
      ],
    });
  }

  ngOnInit() {
    if (this._authService.isAuthenticated()) {
      return this._navigation.home().go();
    }
    this._accountService.getSurveyOptions().subscribe(results => {
      this.surveys = results;
      this.surveys.forEach(item => {
        switch (this.currentLanguage) {
          case 'it': item.name = item.nameIt;
            break;
          case 'de': item.name = item.nameDe;
            break;
          default: item.name = item.nameEn;
        }
      });
    });
  }

  onRegistration() {

    if (this.registerForm.controls['firstEmail'].value === '') {
      this.missingEmailErrorMsg = this._translate.instant('account.errorMsg.missingEmail');
    } else {
      this.missingEmailErrorMsg = null;
    }

    if (this.registerForm.controls['password'].value === '') {
      this.missingPasswordErrorMsg = this._translate.instant('account.errorMsg.missingPassword');
    } else {
      if (this.registerForm.controls['password'].value.length < 6) {
        this.missingPasswordErrorMsg = this._translate.instant('account.errorMsg.passwordShortError');
      } else {
        this.missingPasswordErrorMsg = null;
      }
    }

    if (this.registerForm.controls['retypePassword'].value === '') {
      this.retypePasswordErrorMsg = this._translate.instant('account.errorMsg.missingRepeatPassword');
    } else {
      if (this.registerForm.controls['password'].value !== this.registerForm.controls['retypePassword'].value) {
        this.retypePasswordErrorMsg = this._translate.instant('account.errorMsg.matchPasswordError');
      } else {
        this.retypePasswordErrorMsg = null;
      }
    }

    if (this.registerForm.controls['survey'].value === 0) {
      this.surveyGroupClass = 'row border-red-two';
      this.missingSurveyErrorMsg = this._translate.instant('account.errorMsg.missingSurvey');
    } else {
      this.missingSurveyErrorMsg = null;
      this.surveyGroupClass = 'row';
    }

    if (!this.registerForm.controls['termsConditionsCheck'].valid) {
      this.missingTermsErrorMsg = this._translate.instant('account.errorMsg.missingTerms');
    } else {
      this.missingTermsErrorMsg = null;
    }

    if (this.registerForm.valid) {
      const client = Client.fromDto(this.registerForm.value);
      client.username = client.firstEmail;
      client.answer.surveyId = this.registerForm.controls['survey'].value;
      client.language = this.currentLanguage;
      if (this.registerForm.controls['survey'].value === 7) {
        client.answer.description = this.registerForm.controls['surveyDescription'].value;
      } else {
        client.answer.description = '';
      }
      this._authService.registerNewClientUser(client, this.captchaToken).subscribe(result => {

        this.registerForm.reset({ language: client.language });
        this._translate.use(client.language);
        localStorage.setItem('locale', client.language);
        this._router.navigate(['/login'], { queryParams: { action: 'register' } });
        // this._navigation.login({ queryParams: { action: 'register' } }).go();
      }, error => {
        if (error.status === 600) {
          this.errorArray['firstEmail'] = 'register.errorMsg.emailExist';
        }
      });
    } else {
      this._toastr.error(this._translate.instant('account.errorMsg.invalidForm'));
    }
  }

  checkTermsAndConditions(termsConditionsCheckKey: string) {
    return (group: FormGroup) => {
      const termsAndConditionsCheck = group.controls[termsConditionsCheckKey];
      if (!termsAndConditionsCheck.value) {
        return termsAndConditionsCheck.setErrors({ notTerms: true });
      } else {
        return termsAndConditionsCheck.setErrors(null);
      }
    };
  }

  changeLanguage(language: Language) {
    this.currentLanguage = language;
    this._translate.use(this.currentLanguage.locale);
    localStorage.setItem('locale', this.currentLanguage.locale);
  }

  onLanguageChange(target: any) {
    switch (target.value) {
      case 'de': this.changeLanguage(this.languages[1]);
        break;
      case 'it': this.changeLanguage(this.languages[2]);
        break;
      default: this.changeLanguage(this.languages[0]);
    }
  }

  onCheckBoxClick(id: any) {
    if (id === 7) {
      this.inputActive = true;
    } else {
      this.inputActive = false;
      this.registerForm.get('surveyDescription').setValue('');
    }
  }

  onCaptchaResolved(token: string) {
    this.captchaToken = token;
  }
}
